import { Status } from '../builds/types';
import type {
  Build,
  Project,
  ProjectGroup,
  ProjectGroupRelease,
  Revision,
  RevisionLicense,
  Task,
} from '../model-typings';

export const COUNT_DEFAULT = 50;
export const COUNT_MAX = 100;
export const COUNT_MIN = 25;
export const PAGE_DEFAULT = 1;
export const PAGE_MAX = Infinity;
export const PAGE_MIN = 1;

export const allStatuses: Readonly<Status[]> =
  Object.freeze([Status.Analyzed, Status.Failed, Status.InProgress, Status.Unknown]);

export type BuildWithTask = Build & {
  task?: Task;
};

export type RevisionWithAssociations = Revision & {
  project?: Project;
  licenses?: RevisionLicense[];
  builds?: Build[];
};

export type ProjectDependencyScope = {
  scope: 'project',
  revision: string,
  /**
   * The revision scan to read issues from. Falls back to the revision's latest scan when not set.
   */
  revisionScanId?: number,
};

export type ReleaseGroupDependencyScope = {
  scope: 'releaseGroup',
  projectGroupId: number,
  projectGroupReleaseId: number,
};

export type GlobalDependencyScope = {
  scope: 'global';
  organizationId: number;
}

export type DependencyScope = ProjectDependencyScope | ReleaseGroupDependencyScope | GlobalDependencyScope;

export type ScopeParams = {
  // Project scope parameters
  projectId?: string;
  revisionId?: string;
  // ReleaseGroup scope parameters
  projectGroup?: ProjectGroup;
  projectGroupRelease?: ProjectGroupRelease;
}

/**
 * Given the discrete parameters that define a scope, derive the DependencyScope object
 * @throws Will throw if conflicting parameters are provided
 */
export function deriveDependencyScope(ctx: ScopeParams): DependencyScope | null {
  const { projectId, revisionId, projectGroup, projectGroupRelease } = ctx;
  let isProject;
  let isReleaseGroup;
  let scope: DependencyScope | null = null;
  if (projectGroup && projectGroupRelease) {
    isReleaseGroup = true;
    scope = {
      scope: 'releaseGroup',
      projectGroupId: projectGroup.id,
      projectGroupReleaseId: projectGroupRelease.id,
    };
  } else if (projectId && revisionId) {
    isProject = true;
    const revision = `${projectId}$${revisionId}`;
    scope = { scope: 'project', revision };
  }
  if (isProject && isReleaseGroup) {
    throw new Error('Conflicting parameters: cannot determine scope as both project and release group parameters were provided');
  }
  return scope;
}
